import { useEffect, useMemo } from "react";
import { Authenticated, useTranslate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Card, Flex, Result, Skeleton, Typography } from "antd";
import { Select, Spinner, radius } from "@libertify/ui";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";
import { PageLayout } from "components/PageLayout";
import { useWhatsNewEntry, useWhatsNewIndex } from "./hooks/useWhatsNew";
import { useUnreadWhatsNew } from "./hooks/useUnreadWhatsNew";
import { resolveLocale } from "./config";
import type { WhatsNewEntry } from "./types";

const { Title, Text } = Typography;

const VERSION_PARAM = "v";

const getLocalizedTitle = (entry: WhatsNewEntry, locale: string): string =>
  entry.titles?.[locale as keyof typeof entry.titles] ?? entry.title;

export const WhatsNewPage: React.FC = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: entries,
    isLoading: isLoadingIndex,
    isError: isIndexError,
  } = useWhatsNewIndex();
  const { markAllAsRead } = useUnreadWhatsNew();

  const requestedId = Number(searchParams.get(VERSION_PARAM));
  const selectedId = useMemo(() => {
    if (!entries || entries.length === 0) return undefined;
    const match = entries.find((e) => e.id === requestedId);
    return match?.id ?? entries[0].id;
  }, [entries, requestedId]);

  const selectedEntry = entries?.find((e) => e.id === selectedId);
  const {
    data: markdown,
    isLoading: isLoadingEntry,
    isError: isEntryError,
  } = useWhatsNewEntry(selectedId, locale);

  useEffect(() => {
    if (entries?.length) markAllAsRead();
  }, [entries, markAllAsRead]);

  const handleChange = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set(VERSION_PARAM, String(next));
        return params;
      },
      { replace: true }
    );
  };

  const options = useMemo(
    () =>
      (entries ?? []).map((entry) => ({
        value: entry.id,
        label: `${getLocalizedTitle(entry, locale)} — ${entry.date}`,
      })),
    [entries, locale]
  );

  return (
    <Authenticated key="whats_new_page">
      <PageLayout title={t("whatsNew.title")}>
        <Card>
          {isLoadingIndex && (
            <SpinnerWrapper>
              <Spinner size="lg" />
            </SpinnerWrapper>
          )}

          {isIndexError && (
            <Result
              status="error"
              title={t("whatsNew.errors.indexTitle")}
              subTitle={t("whatsNew.errors.indexSubtitle")}
            />
          )}

          {!isLoadingIndex && !isIndexError && entries?.length === 0 && (
            <Result
              status="info"
              title={t("whatsNew.empty.title")}
              subTitle={t("whatsNew.empty.subtitle")}
            />
          )}

          {entries && entries.length > 0 && (
            <Flex vertical gap="large" style={{ width: "100%" }}>
              <Flex justify="flex-end" style={{ width: "100%" }}>
                <div style={{ width: 300 }}>
                  <Select
                    value={selectedId}
                    options={options}
                    onChange={(value) => handleChange(value as number)}
                    aria-label={t("whatsNew.versionSelectorAriaLabel")}
                  />
                </div>
              </Flex>

              {selectedEntry && (
                <header>
                  <Title level={3} style={{ marginBottom: 4 }}>
                    {getLocalizedTitle(selectedEntry, locale)}
                  </Title>
                  <Text type="secondary">{selectedEntry.date}</Text>
                </header>
              )}

              {isLoadingEntry && <Skeleton active paragraph={{ rows: 6 }} />}

              {isEntryError && (
                <Result
                  status="warning"
                  title={t("whatsNew.errors.entryTitle")}
                  subTitle={t("whatsNew.errors.entrySubtitle")}
                />
              )}

              {!isLoadingEntry && !isEntryError && markdown && (
                <MarkdownBody>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {markdown}
                  </ReactMarkdown>
                </MarkdownBody>
              )}
            </Flex>
          )}
        </Card>
      </PageLayout>
    </Authenticated>
  );
};

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const MarkdownBody = styled.div`
  line-height: 1.6;

  h1,
  h2,
  h3 {
    margin-top: 1.5em;
  }

  img {
    max-width: 100%;
    border-radius: ${radius.box}px;
  }

  pre {
    background: rgba(0, 0, 0, 0.04);
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
  }

  code {
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
`;
